import { SpicesMapSchema, SpicesMap, MAX_QUANTITY_GRAM, Units } from "../data/schema"; // prettier-ignore
import { useCallback, useMemo } from "react";
import { ZodError } from "zod";
import {
  useFilterQuantity,
  useFilterSpices,
  useFilterUnit,
} from "./useFilter";
import { useCache } from "./useCache";
import { useError } from "@/components/ui/overlays/ErrorBanner";

// prettier-ignore
export function useSpices()
{
  const {toggleError} = useError()
  const [, setSpices] = useFilterSpices()
  const [unit] = useFilterUnit()
  const [quantity] = useFilterQuantity()
  const {data: cache} = useCache()

  // Shared validation helper
  const validate = useCallback((m: SpicesMap, prev: SpicesMap): SpicesMap => {
    try {
      const result = SpicesMapSchema.parse(m);
      return result;
    } catch (e: unknown) {

      toggleError(
        e instanceof ZodError ? e.issues[0]?.message : (e as Error).message
      );
      return prev;
    }
  }, [toggleError]);

  // Per-spice operations
  const updateSpice = useCallback(
    (id: number, operation: "add" | "remove" | "increment" | "decrement" | "set", value?: number) => {
      if(!cache) {
        return;
      }
      setSpices((prev) => {
  
        const m = new Map(prev);
        const exists = m.has(id);
        const current = m.get(id)!;
        const portionSize = cache[id]?.portions[unit as Units] * quantity;

        switch (operation) {
          case "add":
            if (exists) {

              return prev;
            }
            const addPortionSize = cache[id]?.portions[unit as Units] * 1;
            m.set(id, {
              name: cache[id]?.name,
              quantity: addPortionSize,
            });
            break;

          case "remove":
            m.delete(id);
            break;

          case "increment": {
            if (!exists) {
              m.set(id, {
                name: cache[id]?.name,
                quantity: portionSize,
              });
            } else {
              m.set(id, {
                ...current,
                quantity: Number((current.quantity + portionSize).toFixed(3)),
              });
            }
            break;
          }

          case "decrement": {
            if (!exists) {
              return prev;
            }
            const newQty = Number((current.quantity - portionSize).toFixed(3));
            if (newQty <= 0) {
              m.delete(id);
            } else {
              m.set(id, { ...current, quantity: newQty });
            }
            break;
          }

          case "set": {
            if (value === undefined || value <= 0) {
              m.delete(id);
            } else {
              m.set(id, {
                name: cache[id]?.name,
                quantity: Number(value.toFixed(3)),
              });
            }
            break;
          }
        }

        const result = validate(m, prev);
        return result;
      });
    },
    [cache, setSpices, unit, quantity, validate]
  );

  // Bulk operations
  const fill = useCallback(() => {
    console.time("[useSpices] fill");
    setSpices((prev) => {
      if (prev.size === 0) {
        return prev;
      }

      const m = new Map(prev);
      const currentTotal = Array.from(m.values()).reduce(
        (sum, spice) => sum + spice.quantity,
        0
      );

      if (currentTotal >= MAX_QUANTITY_GRAM) {
        return prev;
      }

      const scaleFactor = MAX_QUANTITY_GRAM / currentTotal;


      m.forEach((spice, id) => {
        m.set(id, {
          ...spice,
          quantity: Number((spice.quantity * scaleFactor).toFixed(3)),
        });
      });

      const result = validate(m, prev);
      return result;
    });
  }, [setSpices, validate]);

  const clearAll = useCallback(() => {
    setSpices(new Map());
  }, [setSpices]);

  const addNew = useCallback((id: number) => {
    updateSpice(id, "add");
  }, [updateSpice]);

  // Public API
  const api = useMemo(
    () => ({
      updateSpice,
      addSpice: (id: number) => updateSpice(id, "add"),
      removeSpice: (id: number) => updateSpice(id, "remove"),
      increment: (id: number) => updateSpice(id, "increment"),
      decrement: (id: number) => updateSpice(id, "decrement"),
      setSpice: (id: number, quantity: number) => updateSpice(id, "set", quantity),
      fill,
      clearAll,
      addNew,
    }),
    [updateSpice, fill, clearAll, addNew]
  );
  return api;
}
